/**
 * NEXORA AI - Main Application
 * Refactored to use navConfig.js and new navigation components
 * Epic 1.2 - Navigation Premium + Epic 1.3 - Responsive Premium
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Menu, X, ChevronDown, Zap, LogOut, User, Loader2 } from 'lucide-react';
import { ToastProvider } from './contexts/ToastContext';
import AuthProvider, { useAuth } from './providers/AuthProvider';
import { mainNavItems } from './config/navConfig';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingPage from './pages/OnboardingPage';
import OnboardingTestPage from './pages/OnboardingTestPage.jsx';
import ProductsPage from './pages/ProductsPage';
import PublishPage from './pages/PublishPage';
import HistoryPage from './pages/HistoryPage';
import SmartPublishStudio from './pages/SmartPublishStudio';
import Dashboard from './pages/Dashboard';
import GoalsDashboard from './pages/GoalsDashboard';
import HealthDashboard from './pages/HealthDashboard';
import ConnectorsMarketplace from './pages/ConnectorsMarketplace';
import SettingsPage from './pages/SettingsPage';
import BetaDashboard from './pages/BetaDashboard';
import FeedbackManager from './pages/FeedbackManager';
import IncidentManager from './pages/IncidentManager';
import BetaAnalytics from './pages/BetaAnalytics';
import HelpCenter from './pages/HelpCenter';
import SupportAdmin from './pages/SupportAdmin';
import SupportContact from './pages/SupportContact';
import MyTickets from './pages/MyTickets';
import AIProfiles from './pages/AIProfiles';
import ToneManager from './pages/ToneManager';
import PromptStudio from './pages/PromptStudio';
import BrandIdentity from './pages/BrandIdentity';
import AIHistory from './pages/AIHistory';
import PromptLibrary from './pages/PromptLibrary';
import OrganizationsPage from './pages/OrganizationsPage'; // ✅ CORRECTION P0: Missing import
import FeedbackButton from './components/FeedbackButton';
import NotificationCenter from './pages/NotificationCenter';
import Analytics from './pages/Analytics';
import SocialAccounts from './pages/SocialAccounts';
import MobileHeader from './components/layout/MobileHeader';
import AuthCallback from './components/AuthCallback';

/**
 * AppRouter - Handles OAuth callback detection synchronously
 * CRITICAL: Detect session_id BEFORE any other routing logic
 * Conforms to Emergent OAuth Playbook requirements
 */
const AppRouter = () => {
  const location = useLocation();
  
  // ✅ P0 FIX: Synchronous OAuth callback detection (Emergent Playbook)
  // Must check location.hash (reactive) NOT window.location.hash (non-reactive)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/onboarding-test" element={<OnboardingTestPage />} />
      
      {/* Protected Routes */}
      <Route path="/*" element={<ProtectedLayout />} />
    </Routes>
  );
};

const App = () => {
  return (
    <AuthProvider>
      <ToastProvider>
        <Router>
          <AppRouter />
        </Router>
      </ToastProvider>
    </AuthProvider>
  );
};

const ProtectedLayout = () => {
  // ✅ P0 FIX: Authentication barrier
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [expandedItems, setExpandedItems] = useState([]);
  const location = useLocation();
  const { t } = useTranslation('common');

  // ✅ HOOKS AVANT LES RETURNS CONDITIONNELS (Fix "Rendered more hooks" error)
  
  // Responsive breakpoint detection
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close sidebar on mobile when route changes
  useEffect(() => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  }, [location.pathname, isMobile]);

  // ✅ P0 FIX: Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      navigate('/login', { 
        replace: true,
        state: { from: location.pathname }
      });
    }
  }, [loading, isAuthenticated, navigate, location.pathname]);

  // ✅ P0 FIX: Show loading screen during authentication check
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-zinc-400 text-sm">Vérification de l'authentification...</p>
        </div>
      </div>
    );
  }

  // ✅ P0 FIX: Don't render protected content if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const toggleExpand = (itemId) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isActive = (item) => {
    if (location.pathname === item.path) return true;
    if (item.subNav) {
      return item.subNav.some(sub => location.pathname.startsWith(sub.path));
    }
    if (item.tabs) {
      return item.tabs.some(tab => location.pathname.startsWith(tab.path));
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Mobile Header - Shared Component */}
      {isMobile && (
        <MobileHeader
          onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
          isMenuOpen={sidebarOpen}
          title="NEXORA AI"
        />
      )}

      {/* Sidebar Backdrop (Mobile) */}
      <AnimatePresence>
        {sidebarOpen && isMobile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <aside
        className={`fixed top-0 bottom-0 w-[280px] bg-[#0A0A0B] border-r border-zinc-800/50 z-50 transition-transform duration-300 ${
          isMobile ? (sidebarOpen ? 'translate-x-0' : '-translate-x-full') : (isTablet && !sidebarOpen ? '-translate-x-full' : 'translate-x-0')
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6">
            <div className="flex items-center space-x-3 group cursor-pointer">
              <div className="relative">
                <div className="w-11 h-11 gradient-primary rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg shadow-blue-600/30">
                  <Zap className="w-6 h-6 text-white" fill="white" />
                </div>
                <div className="absolute inset-0 gradient-primary rounded-xl blur-xl opacity-30 group-hover:opacity-50 transition-opacity"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight gradient-text">
                  NEXORA AI
                </h1>
                <p className="text-[10px] text-zinc-500 font-medium tracking-wider uppercase">
                  {t('navigation.tagline')}
                </p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 space-y-2 overflow-y-auto pb-32">
            {mainNavItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              const expanded = expandedItems.includes(item.id);
              const hasSubItems = item.subNav || item.tabs;

              return (
                <div key={item.id}>
                  <Link
                    to={item.path}
                    className={`flex items-center justify-between px-4 py-3.5 rounded-xl font-medium transition-all duration-200 group ${
                      active
                        ? 'gradient-primary text-white shadow-lg shadow-blue-600/30'
                        : 'text-zinc-400 hover:bg-zinc-900/50 hover:text-white'
                    }`}
                    onClick={(e) => {
                      // ✅ P1 UX Fix: Sur mobile, premier clic ouvre le sous-menu SANS navigation
                      if (hasSubItems) {
                        // Sur mobile, empêcher navigation si le menu n'est pas déjà ouvert
                        if (isMobile && !expanded) {
                          e.preventDefault();
                        }
                        toggleExpand(item.id);
                      }
                      // Sur desktop ou si menu déjà ouvert : navigation normale
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      <Icon className={`w-5 h-5 transition-transform group-hover:scale-110 ${active ? 'text-white' : ''}`} />
                      <span>{t(item.labelKey)}</span>
                      {active && (
                        <div className="w-2 h-2 rounded-full bg-white animate-pulse ml-2"></div>
                      )}
                    </div>
                    {hasSubItems && (
                      <motion.div
                        animate={{ rotate: expanded ? 180 : 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </motion.div>
                    )}
                  </Link>

                  {/* SubNav */}
                  {hasSubItems && expanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-2 mb-1 space-y-1"
                    >
                      {(item.subNav || item.tabs || []).map((subItem) => {
                        const isSubActive = location.pathname === subItem.path;
                        return (
                          <Link
                            key={subItem.id}
                            to={subItem.path}
                            className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
                              isSubActive
                                ? 'bg-zinc-900/80 text-white border-l-2 border-blue-500 pl-3'
                                : 'text-zinc-500 hover:bg-zinc-900/40 hover:text-zinc-300 border-l-2 border-transparent'
                            }`}
                          >
                            <span>{t(subItem.labelKey)}</span>
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User Section */}
          <UserSection />
        </div>
      </aside>

      {/* Tablet Toggle Button */}
      {isTablet && !sidebarOpen && (
        <button
          onClick={() => setSidebarOpen(true)}
          className="fixed top-4 left-4 z-40 p-2 rounded-xl bg-zinc-900/90 border border-zinc-800/50 text-white hover:bg-zinc-800 transition-colors"
          aria-label="Open sidebar"
        >
          <Menu size={20} />
        </button>
      )}

      {/* Main Content */}
      <main className={`transition-all duration-300 ${isMobile ? 'pt-20' : isTablet && !sidebarOpen ? 'ml-0' : 'ml-[280px]'}`}>
        <Routes>
          {/* ✅ CORRECTION P0 (Sprint C.15 - L1 - Bug Navigation):
              Ajout du préfixe /app/ pour matcher navConfig.js
              Tous les liens du menu utilisent /app/dashboard, /app/products, etc.
          */}
          <Route path="/" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/app/dashboard" element={<Dashboard />} />
          <Route path="/app/products" element={<ProductsPage />} />
          <Route path="/app/onboarding" element={<OnboardingPage />} />
          <Route path="/app/onboarding-test" element={<OnboardingTestPage />} />
          <Route path="/app/smart-publish" element={<SmartPublishStudio />} />
          <Route path="/app/goals" element={<GoalsDashboard />} />
          <Route path="/app/health" element={<HealthDashboard />} />
          <Route path="/app/connectors" element={<ConnectorsMarketplace />} />
          <Route path="/app/publish" element={<PublishPage />} />
          <Route path="/app/history" element={<HistoryPage />} />
          <Route path="/app/settings" element={<SettingsPage />} />
          <Route path="/app/beta-dashboard" element={<BetaDashboard />} />
          <Route path="/app/feedback-manager" element={<FeedbackManager />} />
          <Route path="/app/beta-incidents" element={<IncidentManager />} />
          <Route path="/app/beta-analytics" element={<BetaAnalytics />} />
          <Route path="/app/support" element={<SupportAdmin />} />
          <Route path="/app/support/contact" element={<SupportContact />} />
          <Route path="/app/support/my-tickets" element={<MyTickets />} />
          <Route path="/app/help" element={<HelpCenter />} />
          <Route path="/app/ai-profiles" element={<AIProfiles />} />
          <Route path="/app/tone-manager" element={<ToneManager />} />
          <Route path="/app/prompt-studio" element={<PromptStudio />} />
          <Route path="/app/prompt-library" element={<PromptLibrary />} />
          <Route path="/app/brand-identity" element={<BrandIdentity />} />
          <Route path="/app/ai-history" element={<AIHistory />} />
          <Route path="/app/notifications" element={<NotificationCenter />} />
          <Route path="/app/analytics" element={<Analytics />} />
          <Route path="/app/social-accounts" element={<SocialAccounts />} />
          <Route path="/app/organizations" element={<OrganizationsPage />} />
          <Route path="/app/organizations/new" element={<div className="p-8 text-white">New Organization (Coming Soon)</div>} />
          <Route path="/app/workspaces" element={<div className="p-8 text-white">Workspaces (Coming Soon)</div>} />
          <Route path="/app/ai-studio" element={<Navigate to="/app/ai-profiles" replace />} />
          <Route path="/app/ai-studio/profiles" element={<AIProfiles />} />
          <Route path="/app/ai-studio/prompts" element={<PromptStudio />} />
          <Route path="/app/ai-studio/tone" element={<ToneManager />} />
          <Route path="/app/ai-studio/library" element={<PromptLibrary />} />
          <Route path="/app/ai-studio/history" element={<AIHistory />} />
          <Route path="/app/publishing" element={<Navigate to="/app/publish" replace />} />
          <Route path="/app/publishing/create" element={<PublishPage />} />
          <Route path="/app/publishing/schedule" element={<div className="p-8 text-white">Schedule (Coming Soon)</div>} />
          <Route path="/app/publishing/calendar" element={<div className="p-8 text-white">Calendar (Coming Soon)</div>} />
          <Route path="/app/brand-center" element={<Navigate to="/app/brand-identity" replace />} />
          <Route path="/app/brand-center/identity" element={<BrandIdentity />} />
          <Route path="/app/brand-center/resources" element={<div className="p-8 text-white">Resources (Coming Soon)</div>} />
          <Route path="/app/brand-center/guidelines" element={<div className="p-8 text-white">Guidelines (Coming Soon)</div>} />
          
          {/* Legacy routes (redirect to /app/ equivalents) */}
          <Route path="/products" element={<Navigate to="/app/products" replace />} />
          <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
          <Route path="/analytics" element={<Navigate to="/app/analytics" replace />} />
          
          {/* Wildcard redirect to dashboard */}
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
        <FeedbackButton />
      </main>
    </div>
  );
};

const UserSection = () => {
  const { user, logout } = useAuth();
  const [showDropdown, setShowDropdown] = useState(false);

  const getUserInitials = () => {
    if (!user) return 'U';
    if (user.name) {
      const names = user.name.trim().split(' ');
      if (names.length >= 2) {
        return (names[0][0] + names[names.length - 1][0]).toUpperCase();
      }
      return names[0][0].toUpperCase();
    }
    if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/login';
    }
  };

  return (
    <div className="p-6 border-t border-zinc-800/50">
      <div className="relative">
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="w-full flex items-center space-x-3 p-3 rounded-xl hover:bg-zinc-900/50 transition-colors group"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white font-semibold text-sm flex items-center justify-center ring-2 ring-zinc-800 group-hover:ring-blue-500/50 transition-all">
            {getUserInitials()}
          </div>
          <div className="flex-1 text-left">
            <p className="text-sm font-medium text-white">{user?.name || 'User'}</p>
            <p className="text-xs text-zinc-500">{user?.email || 'email@example.com'}</p>
          </div>
          <ChevronDown className="w-4 h-4 text-zinc-500 group-hover:text-white transition-colors" />
        </button>

        {showDropdown && (
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            <Link
              to="/settings"
              className="flex items-center space-x-2 px-4 py-3 hover:bg-zinc-800 text-zinc-300 hover:text-white transition-colors"
              onClick={() => setShowDropdown(false)}
            >
              <User size={16} />
              <span className="text-sm">Profil</span>
            </Link>
            <button
              onClick={handleLogout}
              className="w-full flex items-center space-x-2 px-4 py-3 hover:bg-red-500/10 text-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut size={16} />
              <span className="text-sm">Déconnexion</span>
            </button>
          </div>
        )}
      </div>

      {/* Version Badge */}
      <div className="mt-3 px-3 py-2 rounded-lg bg-zinc-900/50 border border-zinc-800/50">
        <p className="text-[10px] text-zinc-600 font-semibold tracking-wider uppercase text-center">
          Version 2.0 Pro
        </p>
      </div>
    </div>
  );
};

export default App;
